import React from "react";
import { Box, Chip, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

export interface MilestoneBadgesProps {
  currentStreak: number;
  longestStreak: number;
  size?: "small" | "medium";
  showAll?: boolean; // Show all milestones or only achieved ones
}

interface Milestone {
  days: number;
  icon: React.ReactElement;
  color:
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";
  label: string;
}

const MilestoneBadges: React.FC<MilestoneBadgesProps> = ({
  currentStreak,
  longestStreak,
  size = "small",
  showAll = false,
}) => {
  const { t } = useTranslation();

  const milestones: Milestone[] = [
    {
      days: 7,
      icon: <LocalFireDepartmentIcon fontSize={size} />,
      color: "warning",
      label: t("streaks.milestones.week"),
    },
    {
      days: 30,
      icon: <EmojiEventsIcon fontSize={size} />,
      color: "info",
      label: t("streaks.milestones.month"),
    },
    {
      days: 100,
      icon: <MilitaryTechIcon fontSize={size} />,
      color: "secondary",
      label: t("streaks.milestones.hundred"),
    },
    {
      days: 365,
      icon: <WorkspacePremiumIcon fontSize={size} />,
      color: "error",
      label: t("streaks.milestones.year"),
    },
  ];

  // Determine which milestones are achieved
  const achievedMilestones = milestones.filter(
    (milestone) => longestStreak >= milestone.days
  );

  const displayMilestones = showAll ? milestones : achievedMilestones;

  if (displayMilestones.length === 0 && !showAll) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
      {displayMilestones.map((milestone) => {
        const isAchieved = longestStreak >= milestone.days;
        const isActive = currentStreak >= milestone.days;

        const tooltipText = isAchieved
          ? isActive
            ? t("streaks.milestones.activeTooltip", { days: milestone.days })
            : t("streaks.milestones.achievedTooltip", { days: milestone.days })
          : t("streaks.milestones.lockedTooltip", { days: milestone.days });

        return (
          <Tooltip key={milestone.days} title={tooltipText} arrow>
            <Chip
              icon={milestone.icon}
              label={milestone.days}
              size={size}
              color={isActive ? milestone.color : "default"}
              variant={isAchieved ? "filled" : "outlined"}
              sx={{
                opacity: isAchieved ? 1 : 0.5,
                fontWeight: isActive ? 700 : 500,
                borderWidth: isActive ? 2 : 1,
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default MilestoneBadges;
